<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useContactTimelineStore } from 'dashboard/stores/contactTimeline';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactTimelineMessageList from './ContactTimelineMessageList.vue';
import ContactTimelineFilters from './ContactTimelineFilters.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useContactTimelineStore();
const vuexStore = useStore();

const dialogRef = ref(null);
const isOpen = ref(false);

// FEAT-020: null means "no filter applied" — shows the unfiltered timeline
// from the same store slot the accordion preview already uses. Once set,
// reads/paginates from the store's separate filtered slot instead (see
// contactTimeline.js — filtering never overwrites the preview's cache).
const activeFilters = ref(null);
const isFiltering = computed(() => activeFilters.value !== null);

// Newest-first (store order) reversed to oldest-first — same reading order
// as a single chat thread, matching the accordion preview.
const messages = computed(() =>
  isFiltering.value
    ? [...store.getFilteredMessages(props.contactId)].reverse()
    : [...store.getMessages(props.contactId)].reverse()
);
const hasMore = computed(() =>
  isFiltering.value
    ? store.getFilteredHasMore(props.contactId)
    : store.getHasMore(props.contactId)
);
const isFetchingMore = computed(() => store.uiFlags.isFetchingMore);
const loadMore = () =>
  isFiltering.value
    ? store.fetchMoreFiltered(props.contactId)
    : store.fetchMore(props.contactId);

const hasAnyFilterValue = filters =>
  !!(filters.q || filters.inbox_id || filters.since || filters.until);

const onFiltersChange = filters => {
  if (!hasAnyFilterValue(filters)) {
    activeFilters.value = null;
    return;
  }
  activeFilters.value = filters;
  store.searchFiltered(props.contactId, filters);
};

const open = () => {
  isOpen.value = true;
  dialogRef.value?.open();
};
const close = () => {
  isOpen.value = false;
  dialogRef.value?.close();
};
defineExpose({ open, close });

// BUG-051: clicking a message navigates to its source conversation but left
// the dialog open on top of it. Same reactive route-watch pattern as
// ConversationView.vue's `$store.state.route` watcher (vuex-router-sync
// replaces the whole object on every navigation, so a shallow watch is
// enough).
watch(
  () => vuexStore.state.route,
  () => {
    if (isOpen.value) close();
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="t('CONTACT_PANEL.TIMELINE.DIALOG_TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="max-h-[70vh] overflow-y-auto">
      <ContactTimelineFilters @change="onFiltersChange" />
      <div v-if="hasMore" class="flex justify-center py-2">
        <Button
          :is-loading="isFetchingMore"
          variant="ghost"
          size="sm"
          :label="t('CONTACT_PANEL.TIMELINE.LOAD_MORE')"
          @click="loadMore"
        />
      </div>
      <ContactTimelineMessageList :messages="messages" />
    </div>
  </Dialog>
</template>
